import AddToActionField from '@/components/common/custom-fields/add-to-action-field';
import DialogTemplate from '@/components/common/dialog-template/dialog-template';
import ListStylesContainer from '@/components/common/list-container/list-styles-container';
import { composeUri } from '@/utils/compose';
import { AVATARS, BIG_SUB_MANAGER_CLIENT_TAG, BIG_SUB_MANAGER_SITE_TAG, MANAGER_PLACES, USERS } from '@/utils/CONST';
import { CheckCircleOutlined, PersonOutlined } from '@mui/icons-material';
import LinkIcon from '@mui/icons-material/Link';
import UnpublishedOutlinedIcon from '@mui/icons-material/UnpublishedOutlined';
import { IconButton } from '@mui/material';
import Box from '@mui/material/Box';
import { find } from 'lodash';
import { RefreshButton, TextInput, TopToolbar } from 'ra-ui-materialui';
import { DataTable, FilterButton, List, ReferenceManyField, useTranslate, WithListContext } from 'react-admin';
import { Outlet, useNavigate, useParams } from 'react-router';

interface ManagerPlaceFieldProps {
    clientId?: string;
    siteId?: string;
}

interface ManagerPlaceActionsProps extends ManagerPlaceFieldProps {
    userId: string;
}

const ManagerPlaceStatus = ({ clientId, siteId }: ManagerPlaceFieldProps) => (
    <ReferenceManyField
        reference={MANAGER_PLACES}
        target={'userManager'}
        filter={{ placeOrEvent: composeUri(AVATARS, clientId ?? siteId ?? '') }}
    >
        <WithListContext
            render={({ data }) => (
                <Box sx={{ alignItems: 'center', gap: 1, display: 'flex' }}>
                    {data && data.length > 0 ? (
                        <CheckCircleOutlined color={'success'} />
                    ) : (
                        <UnpublishedOutlinedIcon />
                    )}
                </Box>
            )}
        />
    </ReferenceManyField>
);

const ManagerPlaceActions = ({ userId, clientId, siteId }: ManagerPlaceActionsProps) => (
    <ReferenceManyField
        reference={MANAGER_PLACES}
        target={'userManager'}
        filter={{ placeOrEvent: composeUri(AVATARS, clientId ?? siteId ?? '') }}
    >
        <WithListContext
            render={({ data }) => (
                <>
                    {data && data.length > 0 ? (
                        <IconButton disabled sx={{ p: 0 }}>
                            <LinkIcon />
                        </IconButton>
                    ) : (
                        <AddToActionField
                            avatarUriId={composeUri(AVATARS, clientId ?? siteId ?? '')}
                            userId={userId}
                        />
                    )}
                </>
            )}
        />
    </ReferenceManyField>
);

const UsersListDialog = () => {
    const navigate = useNavigate();
    const translate = useTranslate();
    const { clientId, siteId } = useParams();

    const navigateBack = () => {
        navigate(-1);
    };

    return (
        <DialogTemplate
            data-cy={'compact-users-list-dialog'}
            maxWidth={'lg'}
            icon={<PersonOutlined />}
            open={true}
            onClose={navigateBack}
            title={translate('main.navigation.link_users')}
        >
            <ListStylesContainer>
                <List
                    actions={
                        <TopToolbar sx={{ display: 'flex', alignItems: 'center' }}>
                            <FilterButton />

                            <RefreshButton />
                        </TopToolbar>
                    }
                    component='div'
                    resource={USERS}
                    filter={{
                        'tags[one]': clientId ? [BIG_SUB_MANAGER_CLIENT_TAG] : [BIG_SUB_MANAGER_SITE_TAG],
                        hidden: false,
                        isSemiAnonymous: false,
                    }}
                    sort={{
                        order: 'DESC',
                        field: 'createdAt',
                    }}
                    empty={false}
                    storeKey='compactUsersList'
                    filters={[
                        <TextInput
                            source='lastName'
                            label={'main.navigation.last_name'}
                            variant='standard'
                            resettable
                        />,
                        <TextInput source='firstName' label={'main.navigation.name'} variant='standard' resettable />,
                        <TextInput source='email' label='main.navigation.email' variant='standard' resettable />,
                    ]}
                    disableSyncWithLocation
                >
                    <DataTable bulkActionButtons={false}>
                        <DataTable.Col label={'main.navigation.last_name'} source='lastName' />

                        <DataTable.Col label={'main.navigation.name'} source='firstName' />

                        <DataTable.Col label={'main.navigation.email'} source='email' />

                        <DataTable.Col
                            label={'users.list.association'}
                            render={(record: { tags: string[]; id: string }) => {
                                const sub_manager_type = find(record.tags, (tag) =>
                                    [BIG_SUB_MANAGER_CLIENT_TAG, BIG_SUB_MANAGER_SITE_TAG].includes(tag),
                                );

                                return !!sub_manager_type ? (
                                    <ManagerPlaceStatus clientId={clientId} siteId={siteId} />
                                ) : (
                                    '--'
                                );
                            }}
                        />

                        <DataTable.Col
                            render={(record: { id: string }) => (
                                <ManagerPlaceActions userId={record.id} clientId={clientId} siteId={siteId} />
                            )}
                        />
                    </DataTable>
                </List>
            </ListStylesContainer>

            <Outlet />
        </DialogTemplate>
    );
};

export default UsersListDialog;
